import { Table } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { t } from 'i18next'
import moment from 'moment'
import { MouseEvent, useCallback, useMemo, useState } from 'react'
import Moment from 'react-moment'
import { useNavigate } from 'react-router'
import {
  ConfirmDeleteModal,
  DeleteIconButton,
  ListPageHeader,
  ListPageLayout,
  PageContentDropdown,
  SectionLoadingState,
  TableBoldColumnContainer,
} from '../../components'
import {
  dateFormat,
  DEFAULT_PAGE_SIZE,
  NO_DATA_PLACEHOLDER,
  searchReviewTypes,
} from '../../constants/constants'
import {
  useMediaQuery,
  useSearchReviewDetails,
  useSearchReviews,
} from '../../hooks'
import { notificationService } from '../../services'
import { SearchReview } from '../../store/reducers/searchReviewReducer'
import { routePaths } from '../RootPage'
import {
  searchReviewPanelTypes,
  searchReviewTabs,
} from '../SearchReviewDetailsPage/SearchReviewDetailsPage'

interface ExtendedSearcheReview extends SearchReview {
  key: string
}

const SearchReviewListPage = () => {
  const navigate = useNavigate()
  const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false)
  const [deleteId, setDeleteId] = useState<string | undefined>(undefined)
  const [deleteLoading, setDeleteLoading] = useState(false)

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)

  const isMobile = useMediaQuery('xs')

  const {
    searchReviewList,
    loading,
    exportSearchReviewListCsv,
    searchReviewListTotalResults,
    isDeletable,
    refreshSearchReviewList,
  } = useSearchReviews({
    page,
    pageSize,
    preventFetch: false,
  })

  const handlePageChange = (newPage: number, newPageSize?: number) => {
    setPage(newPage)
    if (newPageSize) {
      setPageSize(newPageSize)
    }
  }

  const { deleteSearchReview } = useSearchReviewDetails({
    searchReviewId: deleteId || '',
    preventFetch: true,
  })

  const formattedData: ExtendedSearcheReview[] =
    useMemo(
      () =>
        searchReviewList?.map((searchReview: SearchReview) => ({
          ...searchReview,
          key: searchReview.id,
        })),
      [searchReviewList]
    ) || []

  const handleClick = (id: string) => {
    navigate(
      `${routePaths.SEARCH_REVIEW_DETAILS}/${id}/${searchReviewPanelTypes.OVERVIEW}/${searchReviewTabs.INFO}`
    )
  }

  const handleSearchReviewListPageExport = () => {
    exportSearchReviewListCsv()
  }

  const handleConfirmDelete = () => {
    if (deleteId) {
      setDeleteLoading(true)
      deleteSearchReview({ searchReviewId: deleteId || '', navigate })
        .then((response: SearchReview) => {
          navigate(routePaths.SEARCH_REVIEW_LIST)
          setDeleteLoading(false)
          notificationService.notificationSuccess({
            messageToDisplay: t(
              'searchReviewListPage.deleteSuccessAlertMessage'
            ),
          })
          refreshSearchReviewList()
        })
        .finally(() => {
          setDeleteLoading(false)
        })
    }
    setConfirmDeleteModalOpen(false)
    setDeleteId(undefined)
  }

  const handleDeleteClick = useCallback((event: MouseEvent, id: string) => {
    event.stopPropagation()
    setDeleteId(id)
    setConfirmDeleteModalOpen(true)
  }, [])

  const columns: ColumnsType<SearchReview> = [
    {
      title: t('Title'),
      dataIndex: 'title',
      key: 'title',
      width: '40%',
      sorter: (a: SearchReview, b: SearchReview) =>
        a.title && b.title ? a.title.localeCompare(b.title) : -1,
      render: (text) => (
        <TableBoldColumnContainer>
          {text || NO_DATA_PLACEHOLDER}
        </TableBoldColumnContainer>
      ),
    },
    {
      title: t('Type'),
      dataIndex: 'reviewType',
      key: 'reviewType',
      width: '10%',
      render: (reviewType) => (
        <>
          {reviewType === searchReviewTypes.HISTORICAL_NC_REVIEW
            ? t('searchReviewListPage.historicalReview')
            : t('searchReviewListPage.literatureReview') || NO_DATA_PLACEHOLDER}
        </>
      ),
    },
    {
      title: t('Owner'),
      dataIndex: 'createdBy',
      key: 'createdBy',
      width: '20%',
      sorter: (a: SearchReview, b: SearchReview) =>
        a.createdBy?.displayName && b.createdBy?.displayName
          ? a.createdBy.displayName.localeCompare(b.createdBy.displayName)
          : -1,
      render: (createdBy) => (
        <>{createdBy.displayName || NO_DATA_PLACEHOLDER}</>
      ),
    },
    {
      title: t('Last Update'),
      dataIndex: 'lastModifiedDate',
      key: 'lastModifiedDate',
      width: '20%',
      sorter: (a: SearchReview, b: SearchReview) =>
        a.lastModifiedDate && b.lastModifiedDate
          ? moment(a.lastModifiedDate).valueOf() -
            moment(b.lastModifiedDate).valueOf()
          : -1,
      render: (lastModifiedDate) => (
        <>
          {lastModifiedDate ? (
            <Moment local format={dateFormat.PRIMARY}>
              {lastModifiedDate}
            </Moment>
          ) : (
            NO_DATA_PLACEHOLDER
          )}
        </>
      ),
    },
    {
      title: '',
      key: 'action',
      align: 'right',
      render: (text, { id }) => (
        <>
          {isDeletable && (
            <DeleteIconButton
              tooltipTitle={t(
                'searchReviewListPage.deleteLiteratureReviewTooltip'
              )}
              onClick={(e: MouseEvent) => handleDeleteClick(e, id)}
            />
          )}
        </>
      ),
    },
  ]

  return loading ? (
    <SectionLoadingState />
  ) : (
    <>
      <ListPageLayout>
        <ListPageHeader
          title={t('searchReviewListPage.title', {
            count: searchReviewListTotalResults,
          })}
          actions={
            <PageContentDropdown
              onExport={handleSearchReviewListPageExport}
              disabled={formattedData.length === 0}
            />
          }
        />
        <Table
          rowClassName="page-list-table-row clickable"
          columns={columns}
          dataSource={formattedData}
          pagination={{
            current: page,
            total: searchReviewListTotalResults,
            pageSize: pageSize,
            position: ['bottomLeft'],
            onChange: handlePageChange,
            showSizeChanger: false,
          }}
          scroll={{
            x: isMobile ? 'max-content' : '100%',
            y: 'calc(100vh - 220px)',
          }}
          onRow={(record, rowIndex) => {
            return {
              onClick: (event) => {
                handleClick(record.id)
              }, // click row
            }
          }}
        />
      </ListPageLayout>
      <ConfirmDeleteModal
        open={confirmDeleteModalOpen}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDeleteModalOpen(false)}
        loading={deleteLoading}
      >
        {t('searchReviewDetailsPage.confirmDeleteModalContent')}
      </ConfirmDeleteModal>
    </>
  )
}

export default SearchReviewListPage
